import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

class ProductInventory extends HTMLElement {
  static URGENCY_COOLDOWN_MS = 30 * 60 * 1000;

  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateInventory);
    this.applyStockUrgency({ decrement: true });
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateInventory);
  }

  /**
   * Updates the inventory.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateInventory = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newInventory = event.detail.data.html.querySelector('product-inventory');

    if (!newInventory) return;

    morph(this, newInventory, { childrenOnly: true });
    this.applyStockUrgency({ decrement: false });
  };

  /**
   * Applies a per-product urgency counter persisted in localStorage.
   * @param {{ decrement: boolean }} options
   */
  applyStockUrgency({ decrement }) {
    if (this.dataset.urgencyEnabled !== 'true') return;

    const textElement = this.querySelector('[data-inventory-text]');
    if (!textElement) return;

    const storageKey = this.getUrgencyStorageKey();
    if (!storageKey) return;

    const fallbackCount = this.parsePositiveNumber(this.dataset.urgencyInitialCount, 1);
    const minCount = this.parsePositiveNumber(this.dataset.urgencyMinCount, 1);
    const decreaseStep = this.parsePositiveNumber(this.dataset.urgencyStep, 1);
    const template = this.dataset.urgencyTemplate || 'Hurry! Only [count] left in stock';
    const savedState = this.parseStoredState(storageKey);
    const savedCount = savedState?.count ?? null;
    const firstProductCount = this.getPerProductInitialCount(fallbackCount, minCount);
    const displayCount = savedCount === null ? firstProductCount : savedCount;
    const now = Date.now();

    textElement.textContent = template.replace('[count]', String(displayCount));

    if (!decrement) {
      if (!savedState) {
        this.saveUrgencyState(storageKey, { count: displayCount, updatedAt: now });
      }
      return;
    }

    if (savedState && now - savedState.updatedAt < ProductInventory.URGENCY_COOLDOWN_MS) {
      return;
    }

    const nextCount = Math.max(minCount, displayCount - decreaseStep);
    this.saveUrgencyState(storageKey, { count: nextCount, updatedAt: now });
  }

  getUrgencyStorageKey() {
    const productId = this.dataset.productId;
    const variantId = this.dataset.variantId || 'default';
    const pagePath = window.location.pathname;

    if (!productId || !pagePath) return null;

    return `product-stock-urgency:${pagePath}:${productId}:${variantId}`;
  }

  /**
   * @param {string} storageKey
   * @returns {{ count: number, updatedAt: number } | null}
   */
  parseStoredState(storageKey) {
    try {
      const rawValue = window.localStorage.getItem(storageKey);
      if (!rawValue) return null;

      const parsedValue = JSON.parse(rawValue);
      const parsedCount = Number(parsedValue?.count);
      const parsedUpdatedAt = Number(parsedValue?.updatedAt);

      if (!Number.isFinite(parsedCount) || parsedCount <= 0) return null;
      if (!Number.isFinite(parsedUpdatedAt) || parsedUpdatedAt <= 0) return null;

      return { count: parsedCount, updatedAt: parsedUpdatedAt };
    } catch (error) {
      return null;
    }
  }

  /**
   * @param {string} storageKey
   * @param {{ count: number, updatedAt: number }} state
   * @returns {void}
   */
  saveUrgencyState(storageKey, state) {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
      // Ignore storage errors in private mode or restricted environments.
    }
  }

  /**
   * @param {string | undefined} value
   * @param {number} fallback
   * @returns {number}
   */
  parsePositiveNumber(value, fallback) {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
  }

  /**
   * Generates a stable fake initial stock per product.
   * @param {number} baseCount
   * @param {number} minCount
   * @returns {number}
   */
  getPerProductInitialCount(baseCount, minCount) {
    const productId = String(this.dataset.productId || '');
    if (!productId) return baseCount;

    let hash = 0;
    for (let i = 0; i < productId.length; i += 1) {
      hash = (hash * 31 + productId.charCodeAt(i)) % 9973;
    }

    const spread = Math.max(1, Math.floor(baseCount * 0.35));
    const offset = hash % (spread + 1);
    return Math.max(minCount, baseCount - offset);
  }
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
